import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card.jsx";
import { Button } from "@/components/ui/button.jsx";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table.jsx";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog.jsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.jsx";
import { Trash2, Search, Loader2, Building2, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast.js";
import {
  useCompanies,
  useDeleteCompany,
  useDetectCompany,
  useCreateCompany,
} from "@/lib/hooks/useCompanies.js";

const CompaniesContext = createContext(null);

function CompaniesProvider({ children }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal states
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [siren, setSiren] = useState("");
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectedCompany, setDetectedCompany] = useState(null);

  // Fetch companies from API
  const {
    data: companiesData,
    isLoading,
    error,
  } = useCompanies(currentPage, rowsPerPage, searchTerm);
  const deleteCompanyMutation = useDeleteCompany();
  const detectCompanyMutation = useDetectCompany();
  const createCompanyMutation = useCreateCompany();

  const companies = companiesData?.results || [];

  // Helper functions
  const toYear = (d) => (d ? String(new Date(d).getFullYear()) : "—");
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

  // Check if a company has complete data
  const hasCompleteData = (c) => {
    // Use the is_complete field from API if available, otherwise fallback to risk score
    if (c?.is_complete !== undefined) {
      return c.is_complete;
    }

    // Fallback: If the company has a risk score, consider it complete
    if (c?.score != null) {
      return true;
    }

    // Fallback: Check missing documents
    const hasAllTaxClearances =
      !c?.missing_tax_years || c.missing_tax_years.length === 0;
    const hasAllBankStatements =
      !c?.missing_statements_count || c.missing_statements_count === 0;

    return hasAllTaxClearances && hasAllBankStatements;
  };

  // Get missing tax clearances
  const getMissingTaxClearances = (c) => {
    return c?.missing_tax_years || [];
  };

  // Get missing bank statements count
  const getMissingBankStatementsCount = (c) => {
    return c?.missing_statements_count || 0;
  };

  // Format date string to readable format
  const formatDateString = (dateString) => {
    try {
      const date = new Date(dateString);
      const monthNames = [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${month} ${year}`;
    } catch (error) {
      return dateString; // Fallback to original string if parsing fails
    }
  };

  // Filter companies by search term
  const filteredCompanies = useMemo(() => {
    if (!searchTerm.trim()) return companies;
    return companies.filter(
      (c) =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.identifier.includes(searchTerm),
    );
  }, [companies, searchTerm]);

  const completeCompanies = useMemo(
    () => filteredCompanies.filter((c) => c?.is_complete === true),
    [filteredCompanies],
  );

  const incompleteCompanies = useMemo(
    () => filteredCompanies.filter((c) => c?.is_complete === false),
    [filteredCompanies],
  );

  useEffect(() => {
    document.title = "Entreprises étudiées | Analyse de risque";
  }, []);

  const handleSelect = (id) => {
   navigate(`/etude?companyId=${id}`);
  };

  const handleDeleteCompany = async (id) => {
    try {
      await deleteCompanyMutation.mutateAsync(id);
      toast({ title: "Entreprise supprimée" });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'entreprise",
        variant: "destructive",
      });
    }
  };

  const handleUploadTaxClearance = (companyId) => {
    navigate(`/company/documents?companyId=${companyId}&tab=fiscal`);
  };

  const handleUploadBankStatement = (companyId) => {
    navigate(`/company/documents?companyId=${companyId}&tab=statements`);
  };

  const handleSirenDetection = async () => {
    const normalizedSiren = siren.replace(/\D/g, "");
    if (normalizedSiren.length !== 9) {
      toast({
        title: "SIREN invalide",
        description: "Le SIREN doit contenir 9 chiffres.",
        variant: "destructive",
      });
      return;
    }

    setIsDetecting(true);
    try {
      const detectedData =
        await detectCompanyMutation.mutateAsync(normalizedSiren);
      setDetectedCompany(detectedData);
      setIsAddCompanyModalOpen(false);
      setIsConfirmModalOpen(true);
    } catch (error) {
      toast({
        title: "Erreur de détection",
        description:
          error.message || "Impossible de détecter l'entreprise avec ce SIREN",
        variant: "destructive",
      });
    } finally {
      setIsDetecting(false);
    }
  };

  const handleCompanyConfirmation = async () => {
    if (!detectedCompany) return;

    try {
      const createdCompany = await createCompanyMutation.mutateAsync({
        ...detectedCompany,
        activities_sector:
          detectedCompany.activities_sector || "Non disponible",
      });

      toast({
        title: "Entreprise créée",
        description:
          "L'entreprise a été créée avec succès. Vous pouvez maintenant télécharger les documents.",
      });

      setIsConfirmModalOpen(false);
      setDetectedCompany(null);
      setSiren("");

      // Navigate to documents page
      navigate(`/company/documents?companyId=${createdCompany.id}`);
    } catch (error) {
      toast({
        title: "Erreur de création",
        description: error.message || "Impossible de créer l'entreprise",
        variant: "destructive",
      });
    }
  };

  const handleCloseAddCompanyModal = () => {
    setIsAddCompanyModalOpen(false);
    setSiren("");
  };

  const handleCloseConfirmModal = (goBack = false) => {
    setIsConfirmModalOpen(false);
    setDetectedCompany(null);
    if (goBack) {
      // Reopen add company modal to allow retry
      setIsAddCompanyModalOpen(true);
    }
  };

  const value = {
    searchTerm,
    setSearchTerm,
    rowsPerPage,
    setRowsPerPage,
    currentPage,
    setCurrentPage,
    isAddCompanyModalOpen,
    setIsAddCompanyModalOpen,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    siren,
    setSiren,
    isDetecting,
    setIsDetecting,
    detectedCompany,
    setDetectedCompany,
    isLoading,
    error,
    toYear,
    formatDate,
    formatDateString,
    hasCompleteData,
    getMissingBankStatementsCount,
    getMissingTaxClearances,
    completeCompanies,
    incompleteCompanies,
    handleSelect,
    handleDeleteCompany,
    handleUploadBankStatement,
    handleUploadTaxClearance,
    handleSirenDetection,
    handleCompanyConfirmation,
    handleCloseAddCompanyModal,
    handleCloseConfirmModal,
    createCompanyMutation,
    deleteCompanyMutation,
    detectCompanyMutation
  };

  return (
    <CompaniesContext.Provider value={value}>
      {children}
    </CompaniesContext.Provider>
  );
}

function useCompaniesContext() {
  return useContext(CompaniesContext);
}

export default function Companies() {
  return (
    <CompaniesProvider>
      <CompaniesComponent />
    </CompaniesProvider>
  );
}

function CompaniesComponent() {
  const { isLoading, error, incompleteCompanies } = useCompaniesContext();

  return (
    <div className="p-6 space-y-6">
      <CompaniesHeader />
      {isLoading && <CompaniesLoading />}
      {error && <CompaniesError />}

      {/* Main content */}
      {!isLoading && !error && (
        <>
          {/* <CompaniesSearchBar /> */}

          <Tabs defaultValue="completes" className="w-full">
            <TabsList>
              <TabsTrigger value="completes">Données complètes</TabsTrigger>
              <TabsTrigger
                value="incomplete"
                className="flex items-center gap-2"
              >
                En phase de complétude
                {incompleteCompanies.length > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {incompleteCompanies.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="completes">
              <CompaniesComplete />
            </TabsContent>

            <TabsContent value="incomplete">
              <CompaniesIncomplete />
            </TabsContent>
          </Tabs>
        </>
      )}

      <CompaniesAddDialog />
      <CompaniesConfirmDialog />
    </div>
  );
}

function CompaniesComplete() {
  const {
    searchTerm,
    rowsPerPage,
    setRowsPerPage,
    formatDate,
    completeCompanies,
    handleSelect,
    handleDeleteCompany,
  } = useCompaniesContext();

  return (
    <Fragment>
      {completeCompanies.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-muted-foreground">
            {searchTerm
              ? "Aucune société trouvée avec ce terme de recherche."
              : "Aucune entreprise pour le moment. Commencez par en ajouter une."}
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Entreprise</TableHead>
                <TableHead className="text-center w-24">Score</TableHead>
                <TableHead className="text-center w-40">
                  Dernière liasse
                </TableHead>
                <TableHead className="text-center w-48">
                  Dernière transaction
                </TableHead>
                <TableHead className="text-right w-64"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {completeCompanies.slice(0, rowsPerPage).map((c) => {
                const score = c.score;
                const last_tax_declaration_date =c.last_tax_declaration_date;
                const last_transaction_date = c.last_transaction_date;
                const score_explanation = c.score_explanation;
                const hasStudy = Boolean(score || last_tax_declaration_date || last_transaction_date,);
                return (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md bg-muted" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{c.name}</div>
                          {/* <div className="text-xs text-muted-foreground truncate">
                            {c.address}, {c.zip_code} {c.town}
                          </div>
                          <div className="text-[11px] text-muted-foreground/80">
                            SIREN: {c.identifier}
                            {c.hasCollectiveProcedure && (
                              <span className="block text-destructive font-medium">
                                procédure collective
                              </span>
                            )}
                          </div> */}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {score != null ? (
                        <div className="flex flex-col items-center gap-1">
                          <div className="font-medium">{score}/100</div>
                        </div>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-center whitespace-nowrap">
                      {last_tax_declaration_date ? formatDate(last_tax_declaration_date) : "—"}
                    </TableCell>
                    <TableCell className="text-center whitespace-nowrap">
                      {last_transaction_date ? formatDate(last_transaction_date) : "—"}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {hasStudy ? (
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" onClick={() => handleSelect(c.id)}>
                            Voir l'étude
                          </Button>
                          <Button size="sm" variant="outline" asChild>
                            <Link to={`/company/documents?companyId=${c.id}`}>
                              Actualiser les données
                            </Link>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                className="p-1 text-red-500 hover:text-red-700"
                                aria-label={`Supprimer ${c.name}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Supprimer l'entreprise ?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Cette action est irréversible. Voulez-vous
                                  supprimer "{c.name}" (SIREN {c.identifier})
                                  ?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteCompany(c.id)}
                                >
                                  Supprimer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" asChild>
                            <Link to={`/company/documents?companyId=${c.id}`}>
                              Compléter le dossier
                            </Link>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                className="p-1 text-red-500 hover:text-red-700"
                                aria-label={`Supprimer ${c.name}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Supprimer l'entreprise ?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Cette action est irréversible. Voulez-vous
                                  supprimer "{c.name}" (SIREN {c.identifier})
                                  ?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteCompany(c.id)}
                                >
                                  Supprimer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </TableCell> 
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {/* <div className="p-4 border-t flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Affichage de {Math.min(rowsPerPage, completeCompanies.length)} sur{" "}
              {completeCompanies.length} entreprises
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Afficher:</span>
              <Select
                value={rowsPerPage.toString()}
                onValueChange={(value) => setRowsPerPage(parseInt(value))}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">lignes</span>
            </div>
          </div> */}
        </div>
      )}
    </Fragment>
  );
}

function CompaniesIncomplete() {
  const {
    searchTerm,
    rowsPerPage,
    setRowsPerPage,
    getMissingBankStatementsCount,
    getMissingTaxClearances,
    incompleteCompanies,
    handleSelect,
    handleDeleteCompany,
    handleUploadBankStatement,
    handleUploadTaxClearance,
  } = useCompaniesContext();

  return (
    <Fragment>
      {incompleteCompanies.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-muted-foreground">
            {searchTerm
              ? "Aucune société trouvée avec ce terme de recherche."
              : "Aucun dossier en cours de complétude."}
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Entreprise</TableHead>
                {/* <TableHead className="text-center w-64 px-8">
                  Documents financiers
                </TableHead>*/}
                <TableHead className="text-right w-48"></TableHead> 
              </TableRow>
            </TableHeader>
            <TableBody>
              {incompleteCompanies.slice(0, rowsPerPage).map((c) => {
                // const missingTaxClearances = getMissingTaxClearances(c);
                // const missingBankStatementsCount =
                //   getMissingBankStatementsCount(c);

                return (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md bg-muted" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{c.name}</div>
                          {/* <div className="text-xs text-muted-foreground truncate">
                            {c.address}, {c.zip_code} {c.town}
                          </div>
                          <div className="text-[11px] text-muted-foreground/80">
                            SIREN: {c.identifier}
                            {c.hasCollectiveProcedure && (
                              <span className="block text-destructive font-medium">
                                procédure collective
                              </span>
                            )}
                          </div> */}
                        </div>
                      </div>
                    </TableCell>
                    {/* <TableCell className="text-center whitespace-nowrap px-8">
                      <div className="flex items-center justify-center gap-4">
                        <div className="flex flex-col items-center gap-1">
                          <div className="text-xs text-muted-foreground">
                            Quittances fiscales
                          </div>
                          {missingTaxClearances.length === 0 ? (
                            <Badge className="rounded-full px-2 text-xs bg-green-500 text-white hover:bg-green-600">
                              Complet
                            </Badge>
                          ) : (
                            <div className="flex flex-wrap gap-1 justify-center">
                              {missingTaxClearances.map((year) => (
                                <Button
                                  key={year}
                                  variant="ghost"
                                  className="h-auto p-1"
                                  onClick={() => handleUploadTaxClearance(c.id)}
                                >
                                  <Badge
                                    variant="destructive"
                                    className="cursor-pointer rounded-full px-2 text-xs hover:bg-red-600"
                                  >
                                    {year}
                                  </Badge>
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <div className="text-xs text-muted-foreground">
                            Relevés bancaires
                          </div>
                          {missingBankStatementsCount === 0 ? (
                            <Badge className="rounded-full px-2 text-xs bg-green-500 text-white hover:bg-green-600">
                              Complet
                            </Badge>
                          ) : (
                            <Button
                              variant="ghost"
                              className="h-auto p-0"
                              onClick={() => handleUploadBankStatement(c.id)}
                            >
                              <Badge
                                variant="destructive"
                                className="cursor-pointer rounded-full px-2 text-xs hover:bg-red-600"
                              >{`${missingBankStatementsCount} manquant(s)`}</Badge>
                            </Button>
                          )}
                        </div>
                      </div>
                    </TableCell> */}
                    <TableCell className="text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" onClick={() => handleSelect(c.id)}>
                          Voir l'étude
                        </Button>
                        <Button size="sm" variant="outline" asChild>
                          <Link to={`/company/documents?companyId=${c.id}`}>
                            Compléter le dossier
                          </Link>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              className="p-1 text-red-500 hover:text-red-700"
                              aria-label={`Supprimer ${c.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Supprimer l'entreprise ?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Cette action est irréversible. Voulez-vous
                                supprimer "{c.name}" (SIREN {c.identifier}) ?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteCompany(c.id)}
                              >
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {/* <div className="p-4 border-t flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Affichage de {Math.min(rowsPerPage, incompleteCompanies.length)}{" "}
              sur {incompleteCompanies.length} entreprises
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Afficher:</span>
              <Select
                value={rowsPerPage.toString()}
                onValueChange={(value) => setRowsPerPage(parseInt(value))}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">lignes</span>
            </div>
          </div> */}
        </div>
      )}
    </Fragment>
  );
}

function CompaniesSearchBar() {
  const { searchTerm, setSearchTerm } = useCompaniesContext();

  return (
    <div className="flex items-center gap-4">
      <div className="relative max-w-md flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher une société... (WIP)"
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          disabled
        />
      </div>
    </div>
  );
}

function CompaniesHeader() {
  const { setIsAddCompanyModalOpen } = useCompaniesContext();

  return (
    <header className="flex items-center justify-between">
      <h1 className="text-xl font-semibold text-foreground">
        Entreprises étudiées
      </h1>
      <Button onClick={() => setIsAddCompanyModalOpen(true)}>
        Nouvelle société
      </Button>
    </header>
  );
}

function CompaniesLoading() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Chargement des entreprises...</p>
      </div>
    </div>
  );
}

function CompaniesError() {
  const { error } = useCompaniesContext();

  return (
    <Card>
      <CardContent className="p-6">
        <div className="text-center text-destructive">
          <p>Erreur lors du chargement des entreprises</p>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error
              ? error.message
              : "Une erreur inattendue s'est produite"}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function CompaniesAddDialog() {
  const {
    isAddCompanyModalOpen,
    setIsAddCompanyModalOpen,
    siren,
    setSiren,
    isDetecting,
    handleSirenDetection,
    handleCloseAddCompanyModal,
  } = useCompaniesContext();

  return (
    <Dialog
      open={isAddCompanyModalOpen}
      onOpenChange={setIsAddCompanyModalOpen}
    >
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nouvelle société</DialogTitle>
          <DialogDescription>
            Entrez le SIREN de l'entreprise à ajouter
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="siren">
              <span className="inline-flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                SIREN de l'entreprise
              </span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="siren"
                value={siren}
                onChange={(e) => setSiren(e.target.value)}
                inputMode="numeric"
                placeholder="Entrez le SIREN à 9 chiffres"
                autoComplete="off"
                disabled={isDetecting}
                required
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && siren.length >= 9 && !isDetecting) {
                    handleSirenDetection();
                  }
                }}
              />
              <Button
                type="button"
                onClick={handleSirenDetection}
                disabled={isDetecting || siren.length < 9}
              >
                {isDetecting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Détection...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Détecter
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCloseAddCompanyModal}
          >
            Annuler
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompaniesConfirmDialog() {
  const {
    isConfirmModalOpen,
    detectedCompany,
    handleCompanyConfirmation,
    handleCloseConfirmModal,
        createCompanyMutation,
    deleteCompanyMutation,
    detectCompanyMutation
  } = useCompaniesContext();

  return (
    <Dialog
      open={isConfirmModalOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleCloseConfirmModal(false);
        }
      }}
    >
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirmer les informations de l'entreprise</DialogTitle>
          <DialogDescription>
            Vérifiez les informations détectées avant de créer l'entreprise
          </DialogDescription>
        </DialogHeader>
        {detectedCompany && (
          <div className="space-y-3 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium">
                  Nom de l'entreprise
                </Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {detectedCompany.name}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">SIREN</Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {detectedCompany.identifier}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">
                  Date d'immatriculation
                </Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {new Date(
                    detectedCompany.immatriculation_date,
                  ).toLocaleDateString("fr-FR")}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Code APE</Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {detectedCompany.activity_code}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">
                  Secteur d'activité
                </Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {detectedCompany.activities_sector || "Non disponible"}
                </div>
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs font-medium">Adresse</Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {detectedCompany.address.line_1}
                  <br />
                  {detectedCompany.address.code} {detectedCompany.address.town}
                  <br />
                  {detectedCompany.country}
                </div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleCloseConfirmModal(true)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <Button
            onClick={handleCompanyConfirmation}
            disabled={createCompanyMutation.isPending}
          >
            {createCompanyMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Création...
              </>
            ) : (
              "Confirmer et créer"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
